import { Counter } from '@/libs/counter'
import { callbackNewAccessToken } from '../libs/deviceApi/callbacks'
import { wait } from '@/libs/wait'
import { backOff, type IBackOffOptions } from 'exponential-backoff'
import log from 'loglevel'
import { randomString } from '@/libs/random'
import { PromiseTimedOutError, promiseWithTimeout } from '@/libs/promise'
import type { DeviceApi } from '@/libs/deviceApi/deviceApi'

const TIME_WAIT_MAX = 60_000 // 60 seconds
const TIME_WAIT_BETWEEN_EACH_CHECK = 100
const NUM_OF_CHECK_TRIES = Math.floor(TIME_WAIT_MAX / TIME_WAIT_BETWEEN_EACH_CHECK)

// There's a known issue where promises fail to resolve when using the JS bridge
// from the compose-webview-multiplatform library. This library is used by ClientSDK.
//
// The Client team has investigated this, and a quick fix isn't possible.
// The issue requires a full re-implementation of the JavaScript bridge.
// You can find the Slack discussion about this here:
// https://opera.slack.com/archives/C08TDCVFBMX/p1752499141373779?thread_ts=1752488404.044939&cid=C08TDCVFBMX
//
// The problem has also been documented in the compose-webview-multiplatform
// GitHub repository: https://github.com/KevinnZou/compose-webview-multiplatform/issues/336
//
// Currently, we're waiting up to one second for the promise to resolve, but
// this is a not an ideal solution. The timeout can be caused by either the
// JavaScript bridge bug or a slow network connection.
const SDK_PROMISE_TIMEOUT = 1000 // 1 second

const ACCESS_TOKEN_INITIAL_VALUE = {
  value: null,
  counterValue: 0
} as const

class AuthTokenError extends Error {}

class AuthTokenEmptyError extends AuthTokenError {}
export class AuthTokenTimeoutError extends AuthTokenError {}

interface GetTokenConfig {
  forceRefresh?: boolean
}

interface AccessTokenWithCounter {
  value: string | null
  counterValue: number
}

export class TokenProviderDeviceApi {
  private accessToken: AccessTokenWithCounter = ACCESS_TOKEN_INITIAL_VALUE

  constructor(
    private deviceApi: DeviceApi,
    private counter: Counter = new Counter()
  ) {
    // Once we get new access token - set it here.
    callbackNewAccessToken.set((token: string | null) => {
      this.accessToken = {
        value: token,
        counterValue: this.counter.increment()
      }
    })
  }

  private async getValidAccessTokenSdkOrTimeout(forceRefresh: boolean): Promise<string> {
    const callId = randomString()

    log.debug(
      `(${callId}) getValidAccessTokenSdkOrTimeout: called with forceRefresh = `,
      forceRefresh
    )

    const resolvingToAccessToken = this.deviceApi.getAccessToken(forceRefresh)

    let accessToken: string
    try {
      accessToken = await promiseWithTimeout(resolvingToAccessToken, SDK_PROMISE_TIMEOUT)
    } catch (error: unknown) {
      if (error instanceof PromiseTimedOutError) {
        log.debug(
          `(${callId}) Promise returned by deviceApi.getAccessToken did not resolve in ${SDK_PROMISE_TIMEOUT}ms`
        )
        throw new Error(
          `Promise returned by deviceApi.getAccessToken did not resolve in ${SDK_PROMISE_TIMEOUT}ms`
        )
      }

      throw error
    }

    log.debug(
      `(${callId}) getValidAccessTokenSdkOrTimeout: resolving with token "${accessToken.slice(0, 8)}..." of length`,
      accessToken.length
    )

    return accessToken
  }

  private async getValidAccessTokenOrTimeout(forceRefresh: boolean): Promise<string> {
    const tokenCounterBeforeRequest = this.accessToken.counterValue

    this.deviceApi.requestAccessToken(forceRefresh)

    for (let currentTry = 1; currentTry <= NUM_OF_CHECK_TRIES; currentTry++) {
      const accessToken = this.accessToken

      // Each time access token is set using callback called by client API then
      // the counter is incremented. This check ensures we keep waiting
      // before client api calls callback and updates the token.
      if (accessToken.counterValue <= tokenCounterBeforeRequest) {
        await wait(TIME_WAIT_BETWEEN_EACH_CHECK)
        continue
      }

      if (accessToken.value === null) {
        throw new AuthTokenEmptyError('DeviceApi returned null instead of token')
      }

      return accessToken.value
    }

    throw new AuthTokenTimeoutError(
      `Timeouted after waiting for access token for ${TIME_WAIT_MAX} ms.`
    )
  }

  // NOTE: the following method tries to fetch access token from client api
  // with a retry.
  async getValidAccessToken(config: GetTokenConfig): Promise<string> {
    // In contrast to Mini/OfA with callback based API, SDK provides
    // straightforward Promise based `getAccessToken` API.
    if (this.deviceApi.type === 'sdk') {
      return backOff(() => this.getValidAccessTokenSdkOrTimeout(config.forceRefresh ?? false), {
        jitter: 'full',
        maxDelay: Infinity,
        numOfAttempts: 10,
        timeMultiple: 2
      })
    }

    // Explicitly passing the whole configuration so library update won't
    // break our configuration.
    const backoffConfig: IBackOffOptions = {
      jitter: 'full',
      startingDelay: 100,
      delayFirstAttempt: false,
      maxDelay: Infinity,
      numOfAttempts: 10,
      retry: () => true,
      timeMultiple: 2
    } as const

    return backOff(
      () => this.getValidAccessTokenOrTimeout(config.forceRefresh ?? false),
      backoffConfig
    )
  }
}
